import React, { Component } from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _filter from 'lodash/filter';
import _cloneDeep from 'lodash/cloneDeep';
import _isEqual from 'lodash/isEqual';
import _findIndex from 'lodash/findIndex';
import _debounce from 'lodash/debounce';
import _findKey from 'lodash/findKey';
import {getNearbySchools, getExplorePlaces, fetchSuburb} from '../../actions';
import {nearbySchools} from '../../selectors/nearbySchools';
import {comparableProperties} from '../../selectors/comparableProperties';
import Loading from '../Loading/Loading';
import { breakpointMdHigh, liveabilityThreshold } from '../../lib/config';
import {PropertyMarker, SchoolMarker, ComparableMarker, ExploreMarker} from './ExploreMarkers';
import {NoData, SchoolList, ComparableList, ExploreList} from './ExploreLists';
import styles from './Explore.scss';
import GoogleMap from '../GoogleMap/GoogleMap';
import { trackLiveability } from '../Liveability/tracking';
import _isEmpty from "lodash/isEmpty";

/*
User Clicks a tab or loads the page =>
A  show loading component
B  load the data, if they didn't click then it's schools as default
C  once the data is loaded,
D  filter it to the default filter for that tab
E  return the filter data for display
F    - update the list
G    - update the map
H    - hide loading component

  A, B, C only happen if the data is not there.
  Changing a tab if the data is there begins at D
  same for filter clicks - start at D

  If the property doesn't have lat/lon, we fetch the suburb and use it's lat/lon
  that happens in step B and resumes as normal in step C
*/

class Explore extends Component {
  constructor(props) {
    super(props);
    this.initMap = this.initMap.bind(this);
    this.changeTabs = this.changeTabs.bind(this);
    this.doFilter = this.doFilter.bind(this);
    this.highlightRow = this.highlightRow.bind(this);
    this.filterInCatchment = this.filterInCatchment.bind(this);

    this.defaultZoom = 14;
    this.comparableFilterMap = {
      'Property': 'Off-Market',
      'RentalListing': 'For Rent',
      'SaleListing': 'For Sale',
      'RecentlySold': 'Recently Sold',
    };
    this.filterTypes = {
      schools: ['All', 'Primary', 'Secondary', 'Government', 'Private'],
      comparables: Object.keys(this.comparableFilterMap).map((key) => this.comparableFilterMap[key]),
      explore: ['Shops', 'Eats', 'Transport'],
    };
    this.state = {
      currentTab: 'schools',
      currentFilter: this.filterTypes.schools[0],
      displayData: [],
      loadingData: true,
      isMobile: true,
      showInCatchment: false,
      map: null,
      coordinates: {},
    };
    this.maps = null;
    this.currentShapes = [];
    this.currentTab = 'schools';

    this.shapeColors = {
      Combined: '#837B7B',
      Preschool: '#5DC26B',
      Primary: '#3399FF',
      Secondary: '#2DCCD3',
      Special: '#87189D',
    };
    this.hasLocation = false;
    this.fetched = {
      schools: false,
      comparables: true,
      explore: false,
    };
    this.mounted = true;
  }
  componentDidMount() {
    // mobile and resize detection
    this.setState({isMobile: (window.innerWidth <= breakpointMdHigh)});
    let dbounce = _debounce(() => {if (this.mounted) this.setState({isMobile: (window.innerWidth <= breakpointMdHigh)});}, 150);
    window.addEventListener('resize', dbounce);

    let coordinates = {};
    if (_get(this.props, 'property.address.location.lat')) {
      this.hasLocation = true;
      if (!this.fetched['schools']) {
        // default data fetch - step B
        this.fetched['schools'] = true;
        this.props.getNearbySchools({lat: this.props.property.address.location.lat, lon: this.props.property.address.location.lon});
      }
      coordinates = {lat: this.props.property.address.location.lat, lon: this.props.property.address.location.lon};
    } else {
      // maybe get the suburb for the property and use it's latlon?
      if (_get(this.props, 'property.address.suburb') && _get(this.props, 'property.address.postCode')) {
        let suburb = this.props.property.address.suburb;
        if (suburb.match(/\-/ig)) suburb = suburb.replace(/\-/ig, '--');
        this.props.fetchSuburb({suburb: suburb, postcode: this.props.property.address.postCode});
      }
      if (this.props.suburb.suburb_detail) {
        coordinates = {lat: this.props.suburb.suburb_detail.address.location.lat, lon: this.props.suburb.suburb_detail.address.location.lon};
      }
    }

    this.setState({coordinates: coordinates});
  }
  componentWillUnmount() {
    this.mounted = false;
  }
  componentDidUpdate(prevProps, prevState) {

    if (_isEmpty(this.state.coordinates) && this.props.suburb.suburb_detail) {
      this.setState({coordinates: {lat: this.props.suburb.suburb_detail.address.location.lat, lon: this.props.suburb.suburb_detail.address.location.lon}});
    }

    if (!this.fetched['schools'] && !_isEmpty(this.state.coordinates)) {
      this.hasLocation = true;
      this.fetched['schools'] = true;
      this.props.getNearbySchools({
        lat: this.state.coordinates.lat,
        lon: this.state.coordinates.lon
      });
    }

    if (!this.hasLocation && _get(this.props, 'property.address.location.lat') || this.props.suburb.suburb_detail) {
      this.hasLocation = true;
    }

    let sameData = _isEqual(prevProps[this.state.currentTab], this.props[this.state.currentTab]);
    if (!sameData && prevProps[this.state.currentTab]) {
      // data has changed
      this.doFilter(this.filterTypes[this.state.currentTab][0]);
    }
    // capture the data returned - step C
    if (this.state.currentTab === 'schools' && this.state.loadingData) {
      this.doFilter(this.filterTypes.schools[0]);
    }
    else if (this.state.loadingData && !prevProps[this.state.currentTab] && this.props[this.state.currentTab]) {
      // it's either explore or comparables
      // comparables needs filtered first, not sure about explore
      if (this.state.currentTab === 'comparables') {
        this.doFilter(this.comparableFilterMap[this.props.property.category]);
      }
      // else if (this.state.currentTab === 'explore') {
      //   this.doFilter(this.filterTypes[this.state.currentTab][0]);
      // }
    }

    if (this.hasLocation && !this.fetched.explore && this.state.map) {
      // this.props.getExplorePlaces({
      //   map: this.state.map,
      //   lat: this.state.coordinates.lat,
      //   lng: this.state.coordinates.lon,
      // });
      this.fetched.explore = true;
    }

    if(this.state.currentTab === 'comparables' && !this.state.displayData.every(np => prevState.displayData?.some(pp => pp.id === np.id))) {
      this.trackLiveabilityImpression(this.state.displayData);
    }
  }

  changeTabs(tabname) {
    this.clearShapes();
    if (this.hasLocation) {
      let cFilter = this.filterTypes[tabname][0];
      let dData = this.props[tabname];
      if (tabname === 'comparables') {
        // get the default filtered data for comparables
        let filter = Object.keys(this.comparableFilterMap)[0];
        cFilter = Object.values(this.comparableFilterMap)[0];
        if (this.props[tabname]) dData = this.props[tabname][filter];
      }
      else if (tabname === 'explore') {
        if (this.props[tabname]) dData = this.props[tabname][cFilter];
      }
      this.setState({currentTab:tabname, currentFilter:cFilter});
      this.currentTab = tabname; // have to do this due to the async of setstate

      // we have the data, update shapes
      this.setState({displayData: dData});
      this.updateMap(dData);
    }
  }

  doFilter(filterName) {
    if (this.hasLocation) {
      // step D
      let dataset = this.props[this.state.currentTab];
      if (dataset) {
        this.setState({currentFilter:filterName, loadingData: false});
        let datakey = '';
        let displayData = [];
        switch (this.state.currentTab) {
          case 'schools':
            // we have two different keys to filter upon
            if (filterName.match(/Primary|Secondary/)) {
              datakey = 'schoolType';
            } else if (filterName.match(/Government|Private/)) {
              datakey = 'schoolSector';
              if (filterName === 'Private') {
                filterName = 'Non-Government';
              }
            } else {
              datakey = 'All';
            }

            if (datakey === 'All') {
              displayData = _cloneDeep(dataset);
            } else {
              displayData = _filter(dataset, [datakey, filterName]);
            }

            this.setState({showInCatchment: false});

            break;
          case 'comparables':
            displayData = dataset[_findKey(this.comparableFilterMap, (cfm_val) => cfm_val === filterName)];
            break;
          // case 'explore':
          //   displayData = dataset[filterName];
          //   break;
        }
        this.setState({displayData: displayData}); // this triggers F
        this.updateMap(displayData); // this triggers G
      }
    }
  }
  highlightRow(item) {
    // I'm the click handler for list items
    let onIndex = _findIndex(this.state.displayData, item);
    let dd = _cloneDeep(this.state.displayData);
    dd.forEach((item, index) => {
      if (index === onIndex) item.showInfo = !item.showInfo;
      else item.showInfo = false;
    });
    this.setState({displayData: dd});
  }
  initMap(map, maps) {
    // called from google maps once it's loaded
    this.setState({map});
    this.maps = maps;
    this.updateMap(this.state.displayData);

    let map_dbounce = _debounce(() => this.fitZoom(this.state.displayData), 150);
    maps.event.addDomListenerOnce(map, 'idle', () => {
      maps.event.addDomListener(window, 'resize', () => {
        map_dbounce();
      });
    });
  }
  createMapOptions() {
    return {
      gestureHandling: 'cooperative' // Change to greedy for mobile emulation
    };
  }
  clearShapes() {
    if (this.currentShapes.length) {
      this.currentShapes.forEach((shape) => shape.setMap(null));
      this.currentShapes = [];
    }
  }
  updateMap(filteredData) {
    // step G
    // clear old markers, add new markers
    // this step is in a race with the maps being available, initMap above is called when the maps are ready
    this.clearShapes();
    if (this.currentShapes.length) {
      this.currentShapes.forEach((shape) => shape.setMap(null));
      this.currentShapes = [];
    }
    if (this.currentTab === 'schools' && this.maps) {
      // add/remove the polygons for displayData
      filteredData.forEach((school) => {
        if (school.hasShape) {
          var currentShape = new this.maps.Polygon({
            paths: school.shape,
            strokeColor: this.shapeColors[school.schoolType],
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: this.shapeColors[school.schoolType],
            fillOpacity: 0.25
          });
          currentShape.setMap(this.state.map);
          this.currentShapes.push(currentShape);
        }
      });
    }
    // make sure they're all on screen
    if (filteredData && filteredData.length && this.maps) this.fitZoom(filteredData);
  }

  fitZoom(filteredData) {
    // fit markers on screen
    // and maybe the infobox as well
    const bounds = new this.maps.LatLngBounds();

    bounds.extend(new this.maps.LatLng(this.state.coordinates['lat'], this.state.coordinates['lon']));
    filteredData.forEach((item) => {
      let latlon = null;
      if (this.currentTab === 'schools') latlon = new this.maps.LatLng(item.latitude, item.longitude);
      else if (this.currentTab === 'comparables') latlon = new this.maps.LatLng(item.address.location.lat, item.address.location.lon);
      // else if (this.currentTab === 'explore') latlon = new this.maps.LatLng(item.geometry.location.lat(), item.geometry.location.lng());
      bounds.extend(latlon);
    });
    this.state.map.fitBounds(bounds);
    if (this.currentTab !== 'explore') {
      this.state.map.setZoom(this.state.map.getZoom()-1);
    }
  }

  trackLiveabilityImpression = (properties) => {
    properties.forEach((property) => {
      if(property.liveability?.livebilityIndex >= liveabilityThreshold.card) {
        trackLiveability('liveability_impression_similar', property);
      }
    });
  }

  getSchoolTextDisplay = (address) => {
    let text = address + ' is in the school catchment zone for ';
    if (this.props['schools']) {
      const schoolsInCatchment = this.props['schools'].filter(({targetInShape}) => targetInShape);
      if (schoolsInCatchment.length === 1) {
        return text + schoolsInCatchment[0].schoolName + '. ';
      } else if (schoolsInCatchment.length > 1) {
        return text + schoolsInCatchment[0].schoolName + ' and ' + schoolsInCatchment[1].schoolName + '. ';
      }
    }
  };

  getShopTextDisplay = () => {
    if (this.props.explore && this.props.explore.Shops) {
      let text = 'The closest shopping centres to the property';
      const shopData = this.props.explore.Shops;
      if (shopData.length === 1) {
        return text + ' includes ' + shopData[0].name + '.';
      } else if (shopData.length > 1) {
        return text + ' include ' + shopData[0].name + ' and ' + shopData[1].name + '.';
      }
    }
  };

  filterInCatchment = (event) => {
    this.doFilter(this.state.currentFilter);
    if (event.target.checked) {
      const schoolsInCatchment = this.state.displayData.filter(({targetInShape}) => targetInShape);
      this.setState({displayData: schoolsInCatchment, showInCatchment: true});
    } else {
      this.setState({showInCatchment: false});
    }
  };

  render() {
    return (
      this.hasLocation ?
        <React.Fragment>
          <div className={this.props.isSuburbProfile ? 'container mb-5' : ''}>
            <h2>What&apos;s {this.props.isSuburbProfile ? `in ${this.props.suburb.suburb_detail.address.suburb} ${this.props.suburb.suburb_detail.address.postcode}` : `around ${this.props.property.address.shortAddress}`}</h2>
            {!this.props.isSuburbProfile &&
            <p>
              {this.getSchoolTextDisplay(this.props.property.address.shortAddress)}
              {this.getShopTextDisplay()}
            </p>}
            <div className="row ml-0 mr-0">
              <div className={'d-flex flex-column col-lg-5 mb-3 mb-lg-0 pt-lg-4 pb-lg-4 ' + styles.lhPanel}>
                <div className="pillTabs pillWide">
                  <div className={'smText' + (this.state.currentTab === 'schools' ? ' pillActive':'')} onClick={() => this.changeTabs('schools')}>Nearby Schools</div>
                  {!this.props.isSuburbProfile &&
                  <div className={'smText' + (this.state.currentTab === 'comparables' ? ' pillActive':'')} onClick={() => this.changeTabs('comparables')}>Comparable Properties</div>}
                  {/*<div className={'smText' + (this.state.currentTab === 'explore' ? ' pillActive':'')} onClick={() => this.changeTabs('explore')}>Explore</div>*/}
                </div>
                {!this.state.isMobile ?
                  <div className="position-relative h-100">
                    {this.state.loadingData ? <Loading context="container" /> :
                      <React.Fragment>
                        <div className="d-md-flex form-group mt-3">
                          <select className="form-control col-4 d-inline-block align-self-center" onChange={(event) => this.doFilter(event.target.value)} value={this.state.currentFilter}>
                            {this.filterTypes[this.state.currentTab].map((filter, index) => <option key={index}>{filter}</option>)}</select>
                          {(this.state.currentTab === 'schools' && !this.props.isSuburbProfile) &&
                              <label className="form-check-label col-8 d-flex">
                                <input className={styles.showInCatchmentCheckbox} type="checkbox" name="inCatchmentFilter" checked={this.state.showInCatchment} onChange={this.filterInCatchment}/>
                                <span className={styles.showInCatchmentText}>Show In Catchment Schools Only</span>
                              </label>}
                        </div>
                        <div className={styles.tabContentContainer}>
                          {this.state.currentTab === 'schools' ?
                            <SchoolList data={this.state.displayData} clickHandler={this.highlightRow} isSuburbProfile={this.props.isSuburbProfile}/>
                            : this.state.currentTab === 'comparables' ?
                              <ComparableList filter={this.state.currentFilter} data={this.state.displayData} clickHandler={this.highlightRow} />
                              // : this.state.currentTab === 'explore' ?
                              //   <ExploreList data={this.state.displayData} clickHandler={this.highlightRow} />
                                : null}
                        </div>
                      </React.Fragment>
                    }
                  </div>
                  : null}
              </div>
              <div className={'col-lg-7 p-0 position-relative ' + styles.rhPanel}>
                <div className={styles.mapPanel}>
                  {this.hasLocation ?
                    <GoogleMap center={{lat: this.state.coordinates.lat, lng: this.state.coordinates.lon}}
                      zoom={this.defaultZoom}
                      options={this.createMapOptions}
                      onGoogleApiLoaded={({map, maps}) => this.initMap(map, maps)}>
                      <PropertyMarker lat={this.state.coordinates.lat} lng={this.state.coordinates.lon} />
                      {this.state.displayData && this.state.displayData.map((item, index) =>
                        this.state.currentTab === 'schools' ? <SchoolMarker key={index} lat={item.latitude} lng={item.longitude} index={index} school={item} clickHandler={() => this.highlightRow(item)}></SchoolMarker> :
                          this.state.currentTab === 'comparables' && item.address && item.address.location ? <ComparableMarker key={index} lat={item.address.location.lat} lng={item.address.location.lon} index={index} comparable={item} clickHandler={() => this.highlightRow(item)}></ComparableMarker> :
                            // this.state.currentTab === 'explore' && item.geometry && item.geometry.location ? <ExploreMarker key={index} lat={item.geometry.location.lat()} lng={item.geometry.location.lng()} explorer={item} clickHandler={() => this.highlightRow(item)}></ExploreMarker> : 
                            null
                      )}
                    </GoogleMap>
                    : <NoData type="Location" />}
                </div>

                {this.state.isMobile ?
                  <div className="">
                    {this.state.loadingData ? <Loading/> :
                      <React.Fragment>
                        <div className="d-flex form-group mt-3">
                          <select className="form-control col-4 d-inline align-self-center" onChange={(event) => this.doFilter(event.target.value)} value={this.state.currentFilter}>
                            {this.filterTypes[this.state.currentTab].map((filter, index) => <option key={index}>{filter}</option>)}
                          </select>
                          {(this.state.currentTab === 'schools' && !this.props.isSuburbProfile) &&
                            <label className="form-check-label col-8 d-flex">
                              <input className={styles.showInCatchmentCheckbox} type="checkbox" name="inCatchmentFilter" checked={this.state.showInCatchment} onChange={this.filterInCatchment}/>
                              <span className={styles.showInCatchmentText}>Show In Catchment Schools Only</span>
                            </label>}
                        </div>
                        <div className={styles.tabContentContainer}>
                          {this.state.currentTab === 'schools' ?
                            <SchoolList data={this.state.displayData} clickHandler={this.highlightRow} isSuburbProfile={this.props.isSuburbProfile} />
                            : this.state.currentTab === 'comparables' ?
                              <ComparableList data={this.state.displayData} clickHandler={this.highlightRow} />
                              // : this.state.currentTab === 'explore' ?
                              //   <ExploreList data={this.state.displayData} clickHandler={this.highlightRow} />
                                : null}
                        </div>
                      </React.Fragment>
                    }
                  </div>
                  : null}

              </div>
            </div>
          </div>
        </React.Fragment>
        : null
    );
  }
}

Explore.propTypes = {
  property: PropTypes.object,
  schools: PropTypes.array,
  comparables: PropTypes.object,
  explore: PropTypes.object,
  suburb: PropTypes.object,
  getNearbySchools: PropTypes.func,
  getExplorePlaces: PropTypes.func,
  fetchSuburb: PropTypes.func,
  isSuburbProfile: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  schools: nearbySchools(state),
  comparables: comparableProperties(state),
  explore: state.explorePlaces.data,
  suburb: state.suburb,
});
const mapDispatchToProps = {getExplorePlaces, getNearbySchools, fetchSuburb};
export default connect(mapStateToProps, mapDispatchToProps)(Explore);
